import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Separator } from "../ui/separator";
import { Clock, ArrowRight } from "lucide-react";
import { ActivityLogEntry, User as UserType } from "../../types";
import { Badge } from "../ui/badge";
import { formatTimestamp } from "../../utils/helpers";

interface ActivityLogProps {
  activityLog: ActivityLogEntry[] | undefined;
  users: UserType[];
}

export default function ActivityLog({ activityLog, users }: ActivityLogProps) {
  // Helper function to get username from user ID
  const getUsername = (userIdOrObject: string | UserType | null | undefined): string => {
    if (!userIdOrObject) return "Not assigned";
    if (typeof userIdOrObject === 'string') {
      const user = users.find(u => u.id === userIdOrObject);
      return user ? user.name : "Unknown User";
    }
    return userIdOrObject.name || "Unknown User";
  };



  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity Log</CardTitle>
      </CardHeader>
      <CardContent>
        {activityLog && activityLog.length > 0 ? (
          <div className="space-y-4">
            {activityLog
              .sort((a, b) =>
                new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
              )
              .map((entry) => (
                <div key={entry.id} className="flex items-start gap-3 pb-4 border-b last:border-0">
                  <div className="bg-muted rounded-full p-1.5 mt-0.5">
                    <Clock size={14} className="text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium mb-2">
                      {entry.changeDescription.includes("|||") || entry.changeDescription.includes(' changed to "') || entry.changeDescription.includes(' assigned to ') ? (
                        <div className="flex flex-col gap-2 mt-1">
                          {entry.changeDescription.split("|||").map((change: string, i: number) => {
                            const match = change.match(/(.*?)\s+(changed to|assigned to|unassigned)\s+"?([^"]*)"?/);
                            if (match) {
                              const [_, field, action, newVal] = match;
                              return (
                                <div key={i} className="flex items-center flex-wrap gap-2">
                                  <Badge variant="outline" className="capitalize bg-white text-gray-700 border-gray-200 shadow-sm">
                                    {field}
                                  </Badge>
                                  <span>{action}</span>
                                  <span className="text-green-600 font-medium text-xs bg-green-50 px-1.5 py-0.5 rounded border border-green-100">
                                    {newVal}
                                  </span>
                                </div>
                              );
                            }
                            return <div key={i}>{change}</div>;
                          })}
                        </div>
                      ) : (
                        entry.changeDescription
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs text-muted-foreground">
                        {formatTimestamp(entry.timestamp)}
                      </p>
                      <Separator orientation="vertical" className="h-3" />
                      <p className="text-xs text-muted-foreground">
                        By {getUsername(entry.user)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <p className="text-muted-foreground italic">No activity history available.</p>
        )}
      </CardContent>
    </Card>
  );
} 